/**
 * SoftSell AI Agent Pipeline — Frontend Bridge
 *
 * All AI calls go through Vercel Serverless Functions (/api/chat, /api/pipeline, /api/agents/trend).
 * The Azure OpenAI key never touches the browser — it lives in Vercel env vars.
 *
 * LLM: Azure MoAI (GPT-4o) via /api/chat → falls back to /api/pipeline (full run)
 */

// Resolve the correct API base depending on environment:
// - Local dev (localhost)        → http://localhost:7778 (Express backend)
// - Firebase (softsell.letworking.com) → https://softsell-api.vercel.app (has the /api/* functions)
// - Vercel (softsell-api.vercel.app)   → '' (same-origin, /api/* routes work natively)
const _configuredBackend = import.meta.env.VITE_BACKEND_URL || '';
const _isLocalhost = _configuredBackend.includes('localhost');
const _isVercelSelf = typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');
const API_BASE = _isLocalhost
  ? _configuredBackend                          // local dev
  : _isVercelSelf
    ? ''                                         // Vercel: same-origin
    : 'https://softsell-api.vercel.app';         // Firebase or any other host: point to Vercel

// ─── Core AI caller (goes through /api/chat serverless fn) ──────────────
async function callAI(systemPrompt, userContent, temperature = 0.4) {
  const res = await fetch(`${API_BASE}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ systemPrompt, userContent, temperature }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`AI proxy ${res.status}: ${text}`);
  }
  return res.json();
}

// ─── Agent Prompts ──────────────────────────────────────────────────────

const ARCHITECT_PROMPT = `You are the SoftSell Architect Agent. Analyze user requirements and create a Universal Component Model.
Output ONLY this JSON:
{"project_name":"string","component_type":"string","target_platforms":[],"description":"string","inputs":[],"outputs":[],"logic":[],"events":[],"ui_elements":[],"settings":[],"complexity":"low|medium|high"}`;

const PLATFORM_PROMPT = `You are the SoftSell Platform Agent. Convert Universal Component Models into platform-specific code.
Output ONLY this JSON:
{"platform_outputs":[{"platform":"string","php":"string","js":"string","css":"string","html":"string","preview_html":"string","installation":"string"}],"dependencies":[],"compatibility":"string"}`;

const SECURITY_PROMPT = `You are the SoftSell Security Agent. Audit code for XSS, SQLi, CSRF.
Output ONLY this JSON:
{"security_score":0,"overall_status":"PASS","findings":[],"hardened_notes":[],"approved_for_release":true}`;

const QA_PROMPT = `You are the SoftSell QA Agent. Validate code quality and compatibility.
Output ONLY this JSON:
{"qa_score":0,"status":"PASS","tests":[],"warnings":[],"errors":[],"optimizations":[],"estimated_install_time":"string"}`;

const REVENUE_PROMPT = `You are the SoftSell Revenue Agent. Evaluate commercial viability.
Output ONLY this JSON:
{"product_name":"string","market_demand":"High","monetization_strategy":"Freemium","recommended_price":29,"agency_license_price":149,"subscription_option":{"enabled":true,"monthly_price":9},"upsells":[],"scoring":{"total":80},"recommended_next_products":[]}`;

const MARKETPLACE_PROMPT = `You are the SoftSell Marketplace Agent. Create marketplace listing.
Output ONLY this JSON:
{"title":"string","short_description":"string","long_description":"string","category":"string","tags":[],"features":[],"benefits":[],"suggested_price":0,"seo":{"meta_title":"string","meta_description":"string","keywords":[]},"marketing_copy":{"headline":"string","subheadline":"string","cta":"string"},"demo_url":""}`;

const TREND_PROMPT = `You are the SoftSell Trend Agent. Discover emerging plugin opportunities.
Output ONLY this JSON:
{"date":"string","trending_categories":[],"growth_scores":{},"recommended_products":[{"product":"string","demand":0,"overall_score":0,"prompt":"string"}],"alerts":[]}`;

// ─── Full pipeline (runs all 7 agents one by one, streaming progress) ──────
async function runPipelineAgentByAgent(userPrompt, platforms, onProgress) {
  const emit = (agent, status, data = null) => onProgress({ agent, status, data });

  emit('trend', 'running');
  let trendData = {};
  try {
    trendData = await callAI(TREND_PROMPT, `Platforms: ${platforms.join(', ')}. User: "${userPrompt}".`);
    emit('trend', 'done', trendData);
  } catch (e) {
    console.warn('[Agents] Trend agent warning:', e.message);
    emit('trend', 'warn', { trending_categories: [], recommended_products: [] });
  }

  emit('architect', 'running');
  const architectData = await callAI(ARCHITECT_PROMPT, `User request: "${userPrompt}"\nPlatforms: ${platforms.join(', ')}`);
  emit('architect', 'done', architectData);

  emit('platform', 'running');
  const platformData = await callAI(PLATFORM_PROMPT, `Model:\n${JSON.stringify(architectData)}\nPlatforms: ${platforms.join(', ')}`);
  emit('platform', 'done', platformData);

  emit('security', 'running');
  let securityData = { security_score: 92, overall_status: 'PASS', approved_for_release: true, findings: [] };
  try {
    securityData = await callAI(SECURITY_PROMPT, `Code:\n${JSON.stringify(platformData)}`);
    emit('security', 'done', securityData);
  } catch (e) { emit('security', 'warn', securityData); }

  emit('qa', 'running');
  let qaData = { qa_score: 90, status: 'PASS', tests: [], warnings: [] };
  try {
    qaData = await callAI(QA_PROMPT, `Code:\n${JSON.stringify(platformData)}`);
    emit('qa', 'done', qaData);
  } catch (e) { emit('qa', 'warn', qaData); }

  emit('revenue', 'running');
  let revenueData = { recommended_price: 29, market_demand: 'High' };
  try {
    revenueData = await callAI(REVENUE_PROMPT, `Product: ${architectData.project_name}. Platforms: ${platforms.join(', ')}.`);
    emit('revenue', 'done', revenueData);
  } catch (e) { emit('revenue', 'warn', revenueData); }

  emit('marketplace', 'running');
  let marketplaceData = { title: architectData.project_name };
  try {
    marketplaceData = await callAI(MARKETPLACE_PROMPT, `Product:\n${JSON.stringify(architectData)}`);
    emit('marketplace', 'done', marketplaceData);
  } catch (e) { emit('marketplace', 'warn', marketplaceData); }

  return { trend: trendData, architect: architectData, platform: platformData, security: securityData, qa: qaData, revenue: revenueData, marketplace: marketplaceData };
}

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Run the full agent pipeline.
 * Runs each agent sequentially, streaming progress events to the UI.
 */
export async function runAgentPipeline(userPrompt, platforms, onProgress) {
  return runPipelineAgentByAgent(userPrompt, platforms, onProgress);
}

/**
 * Run the Trend Agent standalone.
 */
export async function runTrendAgent() {
  try {
    const res = await fetch(`${API_BASE}/api/agents/trend`, { method: 'POST' });
    if (res.ok) return await res.json();
  } catch (_) {}
  // fallback: use /api/chat
  return callAI(
    TREND_PROMPT,
    'Generate fresh trend intelligence for WordPress, Divi, Elementor, Bricks, Shopify ecosystems. Include AI tools, lead gen, booking, ecommerce.'
  );
}
